<template>
  <modal-wrap
    :id="id"
    ref="integrationsOrders"
    :name="modalName"
    :header-class="'integrations-orders-header'"
    body-class="integrations-orders-modal"
    :hide-footer="true"
    size="lg"
  >
    <template #modal-title>
      <div class="d-flex justify-content-between align-items-center">
        <h4 class="mb-0">Orders</h4>
      </div>
    </template>
    <template #default>
      <div class="w-100">
        <div class="w-100 d-flex justify-content-between mb-3 filters">
          <input v-model="search" type="text" class="form-control" placeholder="External Identifier" />
        </div>
        <easy-data-table
          ref="dataTable"
          v-model:server-options="serverOptions"
          table-class-name="vue3-easy-data-table table-shadow"
          :server-items-length="itemsLength"
          :loading="loading"
          :headers="headers"
          :items="items"
          :prevent-context-menu-row="false"
          :fixed-header="false"
          no-hover
          alternating
          hide-footer
          @update:server-options="updateServerOptions"
        >
          <template #loading>
            <b-spinner class="mt-5" variant="success" />
          </template>
          <template #no-data>
            <div style="text-align: center">No data available</div>
          </template>
          <template #item="{ column, item }: { column: keyof ExternalOrder; item: ExternalOrder }">
            <span v-if="column === 'internalId'">
              <router-link
                v-if="item.internalId && [WOOCOMMERCE, MAGENTO].includes(modalData.type)"
                :to="detailsRoute(item.internalId)"
              >
                {{ item.internalId }}
              </router-link>
              <span v-else-if="!item.internalId && [WOOCOMMERCE, MAGENTO].includes(modalData.type)">
                {{ item.internalId }}
              </span>
              <router-link v-else-if="item.reference" :to="detailsRoute(item.reference)">
                {{ item.reference }}
              </router-link>
              <span v-else>{{ item.internalId }}</span>
            </span>
            <span v-if="column === 'externalId'">{{ item.externalId }}</span>
            <span v-if="column === 'status'">{{ item.status }}</span>
            <span v-if="column === 'error'">{{ item.error }}</span>
          </template>
        </easy-data-table>

        <TablePagination
          v-model:server-options-ref="serverOptions"
          v-model:data-table-ref="dataTable"
          :server-items-length="itemsLength"
        />
      </div>
    </template>
  </modal-wrap>
</template>

<script lang="ts">
import type { ExternalOrder } from '@/types/Models/Integration'
import { BSpinner } from 'bootstrap-vue-next'
import { defineComponent, ref, computed, watch, type Ref, type ComputedRef } from 'vue'
import { WOOCOMMERCE, MAGENTO } from '@/const/IntegrationTypes'
import { INTEGRATION_ORDERS } from '@/const/ModalTypes'
import { useAccountStore } from '@/store/account'
import { useCustomersIntegrationsStore } from '@/store/customers/integrations'
import { useVueNextModalStore } from '@/store/vueNextModal'
import { CUSTOMER_ASSIGNMENTS_DETAILS, STAFF_ASSIGNMENTS_DETAILS } from '@/types/router'
import EasyDataTable, { type Header, type ServerOptions } from 'vue3-easy-data-table'
import ModalWrap from '@/views/Modals/ModalWrap.vue'
import TablePagination from '@/views/Components/Elements/Table/TablePagination.vue'

export default defineComponent({
  name: 'IntegrationsOrdersModal',
  components: {
    BSpinner,
    TablePagination,
    ModalWrap,
    EasyDataTable,
  },
  props: {
    id: {
      type: String,
      required: true,
    },
  },
  setup() {
    const accountStore = useAccountStore()
    const modalStore = useVueNextModalStore()
    const customersIntegrationsStore = useCustomersIntegrationsStore()

    const dataTable: Ref = ref()
    const modalName = INTEGRATION_ORDERS
    const modalData: ComputedRef = computed(() => modalStore.modalPropsByName(modalName))
    const items: ComputedRef<ExternalOrder[]> = computed(() => customersIntegrationsStore.ordersList)
    const itemsLength: ComputedRef<number> = computed(() => customersIntegrationsStore.ordersCount)
    const isStaff: ComputedRef<boolean> = computed(() => accountStore.isStaff)
    const loading: Ref<boolean> = ref(false)
    const checkedOrders: Ref<ExternalOrder[]> = ref([])
    const serverOptions: Ref<ServerOptions> = ref({ page: 1, rowsPerPage: 25 })
    const search: Ref<string> = ref('')

    const headers: Header[] = [
      { text: 'External ID', value: 'externalId' },
      { text: 'Assignment Ref', value: 'internalId' },
      { text: 'Status', value: 'status' },
      { text: 'Error', value: 'error' },
    ]

    const loadFromServer = () => {
      customersIntegrationsStore.resetOrders()
      if (!modalData.value.customerId || !modalData.value.integrationId) {
        return
      }
      loading.value = true
      customersIntegrationsStore
        .orderMapping({
          id: modalData.value.integrationId,
          customerId: modalData.value.customerId,
          params: {
            externalId: search.value,
            page: serverOptions.value.page,
            limit: serverOptions.value.rowsPerPage,
          },
        })
        .finally(() => {
          loading.value = false
        })
    }

    const updateServerOptions = (newOptions: ServerOptions) => {
      serverOptions.value = newOptions
    }
    const detailsRoute = (ref: string) => ({
      name: isStaff.value ? STAFF_ASSIGNMENTS_DETAILS : CUSTOMER_ASSIGNMENTS_DETAILS,
      params: { id: ref },
    })

    watch(serverOptions, loadFromServer)
    watch(modalData, loadFromServer)
    watch(search, loadFromServer)

    return {
      dataTable,
      items,
      loading,
      headers,
      checkedOrders,
      serverOptions,
      itemsLength,
      modalName,
      modalData,
      search,
      detailsRoute,
      updateServerOptions,
      WOOCOMMERCE,
      MAGENTO,
    }
  },
})
</script>
